import React, { useState, useEffect } from 'react';
import { Clock, Users, CheckCircle, Plus, AlertCircle, CalendarDays, Plane, BarChart3, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { BalanceRing } from '../components/dashboard/BalanceRing';
import { WhosOut } from '../components/dashboard/WhosOut';
import { UpcomingHolidays } from '../components/dashboard/UpcomingHolidays';
import { ApprovalPanel } from '../components/dashboard/ApprovalPanel';
import { fmtRange } from '../utils/format';
import { RequestForm } from '../components/requests/RequestForm';
import apiService from '../services/api';
import { parseISO, isWeekend, format, differenceInCalendarDays, startOfDay } from 'date-fns';

// ─────────────────────────────────────────────────────────────
// Schedule helpers
// ─────────────────────────────────────────────────────────────
function parseWorkDays(sched) {
  if (!sched) return null;
  let wd = sched.days ?? sched.workDays ?? sched.work_days;
  if (wd === undefined || wd === null) return null;
  if (typeof wd === 'string') {
    try { wd = JSON.parse(wd); } catch { return null; }
  }
  if (!Array.isArray(wd) || wd.length < 7) return null;
  return wd.map(v => v === true || v === 1 || v === '1' || v === 'true');
}

function getActiveSchedule(empSchedules, day) {
  if (!empSchedules?.length) return null;
  const dateStr = day.toISOString().slice(0, 10);
  const getFrom = s =>
    (s.effectiveFrom ?? s.effective_from ?? s.from ?? '1970-01-01').slice(0, 10);
  const past = empSchedules.filter(s => getFrom(s) <= dateStr);
  if (past.length) return past.sort((a, b) => getFrom(b).localeCompare(getFrom(a)))[0];
  return [...empSchedules].sort((a, b) => getFrom(a).localeCompare(getFrom(b)))[0];
}

function isNonWorkingDay(empSchedules, day) {
  if (!empSchedules?.length) return false;
  const sched = getActiveSchedule(empSchedules, day);
  const workDays = parseWorkDays(sched);
  if (!workDays) return false;
  const jsDay = day.getDay();
  const schedIdx = jsDay === 0 ? 6 : jsDay - 1;
  return !workDays[schedIdx];
}

function getHoursPerDay(empSchedules, day) {
  const sched = getActiveSchedule(empSchedules, day);
  const raw = parseFloat(sched?.hoursPerDay ?? sched?.hours_per_day ?? FULL_DAY_HOURS);
  return isNaN(raw) ? FULL_DAY_HOURS : raw;
}

// ─────────────────────────────────────────────────────────────
// Half-day helpers
// ─────────────────────────────────────────────────────────────
const HALF_DAY_AM_HOURS = 3;
const HALF_DAY_PM_HOURS = 4.5;
const FULL_DAY_HOURS = 7.5;

function isHalfDayLeave(r) {
  if (!r) return false;
  if (r.halfDay === true || r.isHalfDay === true) return true;
  if (r.halfDayPeriod && r.halfDayPeriod !== null) return true;
  if (r.days !== undefined && parseFloat(r.days) === 0.5) return true;
  return false;
}

function getHalfDayPeriod(r) {
  const period = r?.halfDayPeriod ?? r?.period ?? null;
  if (!period) return null;
  const p = String(period).toLowerCase();
  if (p === 'am' || p === 'morning') return 'AM';
  if (p === 'pm' || p === 'afternoon') return 'PM';
  return null;
}

// ─── South Africa Team members don't have a leave allowance/balance
// tracked in this system at all. Checks the same field shapes as
// Calendar.jsx / RequestForm.jsx — in particular the `departments`
// array (each entry has `departmentName`) that getCompanyUsers()
// actually returns, plus a few flat-field fallbacks in case
// getMe() shapes the current user differently. ───
function isSouthAfricaTeam(user) {
  const deptNames = [
    user?.dept,
    user?.department,
    user?.department?.name,
    user?.team,
    user?.team?.name,
    ...(Array.isArray(user?.departments) ? user.departments.map(d => d?.departmentName ?? d?.name) : []),
  ];
  return deptNames.some(v => typeof v === 'string' && v.toLowerCase().includes('south africa'));
}

// ─────────────────────────────────────────────────────────────
// Allowance Calculator
// ─────────────────────────────────────────────────────────────
// ─── UPDATED: accepts `year` and filters requests to that year only,
// matching Calendar.jsx's calcAllowance so both views agree ───
function calcDashboardAllowance({ allowance, schedules, requests, leaveTypesMap = {}, year }) {
  // Server-calculated balance (leave engine) — matches the calendar and the
  // mobile app, and understands accrual, carry-over and the person's policy
  const server = allowance?.balance;
  if (server && typeof server === 'object' && !server.noBalanceTracking) {
    const fmtD = d => {
      const v = Number(d) || 0;
      const whole = Math.floor(Math.abs(v));
      const half = Math.abs((Math.abs(v) - whole) - 0.5) < 0.01;
      const sign = v < 0 ? '-' : '';
      if (half) return whole > 0 ? `${sign}${whole}½d` : `${sign}½d`;
      return `${sign}${Math.round(Math.abs(v) * 10) / 10}d`;
    };
    const fmtHr = h => { const v = Math.round((Number(h) || 0) * 10) / 10; return `${v}h`; };
    const inHours = server.unit === 'hours';
    const pick = b => (inHours ? b?.hours : b?.days) ?? 0;
    const fmt = inHours ? fmtHr : fmtD;
    return {
      totalLabel:   fmt(pick(server.total)),
      usedLabel:    fmt(pick(server.used)),
      remainLabel:  fmt(pick(server.available)),
      pendingLabel: pick(server.pending) > 0 ? fmt(pick(server.pending)) : null,
      ringUsed:     Math.round(server.used?.hours ?? 0),
      ringTotal:    Math.round(Math.max(server.total?.hours ?? 0, 0)),
      periodLabel:  server.period?.label ?? null,
      accruing:     server.method === 'accrual' && server.mode !== 'per_employee',
    };
  }

  const displayUnit = allowance?.displayUnit ?? allowance?.display_unit ?? 'days';
  const defaultHpd = schedules.length ? getHoursPerDay(schedules, new Date()) : FULL_DAY_HOURS;

  // ─── UPDATED: default to 116 (was 0) to match Calendar's fallback ───
  const totalHours =
    parseFloat(allowance?.allowanceHours ?? allowance?.allowance_hours ?? 116) +
    parseFloat(allowance?.carryForwardHours ?? allowance?.carry_forward_hours ?? 0) +
    parseFloat(allowance?.timeInLieuHours ?? allowance?.time_in_lieu_hours ?? 0);

  // ─── Filter to current year only so previous years don't eat into
  // this year's balance (matches Calendar.jsx's calcAllowance) ───
  const yearStr = year ? String(year) : null;
  const yearScopedRequests = requests.filter(r => {
    if (!yearStr) return true;
    const fromYear = r.from?.slice(0, 4);
    const toYear   = r.to?.slice(0, 4);
    return fromYear === yearStr || toYear === yearStr;
  });

  function deductionFor(r) {
    if (isHalfDayLeave(r)) {
      let hours;
      if (r.hours != null && !isNaN(parseFloat(r.hours))) {
        hours = parseFloat(r.hours);
      } else {
        const period = getHalfDayPeriod(r);
        hours = period === 'PM' ? HALF_DAY_PM_HOURS : HALF_DAY_AM_HOURS;
      }
      return { hours, days: 0.5 };
    }

    if (r.hours != null && !isNaN(parseFloat(r.hours)) && parseFloat(r.hours) > 0) {
      const storedHours = parseFloat(r.hours);
      const storedDays = r.days != null && !isNaN(parseFloat(r.days))
        ? parseFloat(r.days)
        : storedHours / FULL_DAY_HOURS;
      return { hours: storedHours, days: storedDays };
    }

    let h = 0, d = 0;
    const start = parseISO(r.from);
    const end = parseISO(r.to);
    const cur = new Date(start);
    while (cur <= end) {
      if (!isWeekend(cur) && !isNonWorkingDay(schedules, cur)) {
        h += FULL_DAY_HOURS;
        d += 1;
      }
      cur.setDate(cur.getDate() + 1);
    }
    return { hours: h, days: d };
  }

  function isDeductible(r) {
    const typeName = r.type ?? r.leaveType?.name;
    if (!typeName) return true;
    const cfg = leaveTypesMap[typeName];
    return cfg ? cfg.affectsBalance !== false : true;
  }

  function sumDeductions(reqs) {
    return reqs.reduce((acc, r) => {
      const { hours, days } = deductionFor(r);
      return { hours: acc.hours + hours, days: acc.days + days };
    }, { hours: 0, days: 0 });
  }

  // ─── UPDATED: use yearScopedRequests instead of the raw requests list ───
  const deductible = yearScopedRequests.filter(isDeductible);
  const used = sumDeductions(deductible.filter(r => r.status === 'approved'));
  const pending = sumDeductions(deductible.filter(r => r.status === 'pending'));

  const remainHours = Math.max(0, totalHours - used.hours);
  const totalDays = totalHours / defaultHpd;
  const remainDays = Math.max(0, totalDays - used.days);
  const usedDays = Math.min(totalDays, used.days);

  function fmtDays(days) {
    const whole = Math.floor(days);
    const half = Math.abs((days - whole) - 0.5) < 0.01;
    if (half) return whole > 0 ? `${whole}½d` : '½d';
    return `${Math.round(days)}d`;
  }
  const fmtH = h => `${h % 1 === 0 ? h : h.toFixed(1)}h`;

  if (displayUnit === 'hours') {
    return {
      totalLabel: fmtH(totalHours),
      usedLabel: fmtH(used.hours),
      remainLabel: fmtH(remainHours),
      pendingLabel: pending.hours > 0 ? fmtH(pending.hours) : null,
      ringUsed: Math.round(used.hours),
      ringTotal: Math.round(totalHours),
    };
  }

  return {
    totalLabel: fmtDays(totalDays),
    usedLabel: fmtDays(usedDays),
    remainLabel: fmtDays(remainDays),
    pendingLabel: pending.days > 0 ? fmtDays(pending.days) : null,
    ringUsed: Math.round(used.hours),
    ringTotal: Math.round(totalHours),
  };
}

// ─────────────────────────────────────────────────────────────
// Per-day breakdown of a request, used for the "leave this year"
// chart and type breakdown. A half day counts 0.5 on its date; a
// longer request counts 1 for each working day it covers.
// ─────────────────────────────────────────────────────────────
function workingDayEntries(r, schedules) {
  try {
    const start = parseISO(r.from);
    if (isHalfDayLeave(r)) return [{ date: start, amount: 0.5 }];
    const end = parseISO(r.to ?? r.from);
    const out = [];
    const cur = new Date(start);
    while (cur <= end) {
      if (!isWeekend(cur) && !isNonWorkingDay(schedules, cur)) out.push({ date: new Date(cur), amount: 1 });
      cur.setDate(cur.getDate() + 1);
    }
    return out;
  } catch {
    return [];
  }
}

function fmtDayCount(n) {
  const whole = Math.floor(n);
  const half = Math.abs((n - whole) - 0.5) < 0.01;
  if (half) return whole > 0 ? `${whole}½d` : '½d';
  return `${Math.round(n)}d`;
}

const TYPE_FALLBACK_COLORS = ['#FFC81E', '#FFEFB3', '#F5A300', '#C98A00', '#E3DCCB', '#FFE08A'];

// ─────────────────────────────────────────────────────────────
// Main Dashboard Component
// ─────────────────────────────────────────────────────────────
export default function Dashboard() {
  const { requests, currentUser, summary, isApprover } = useApp();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  const [allowance, setAllowance] = useState(null);
  const [schedules, setSchedules] = useState([]);
  const [myRequests, setMyRequests] = useState(null);
  const [leaveTypesMap, setLeaveTypesMap] = useState({});
  const [loading, setLoading] = useState(true);

  const year = new Date().getFullYear();

  // No balance tracked for this person (their department doesn't track
  // allowance): the server says so; the old department-name check stays as
  // a fallback for older APIs.
  const isSA = allowance?.balance?.noBalanceTracking === true || (!allowance && isSouthAfricaTeam(currentUser));

  useEffect(() => {
    if (!currentUser?.id) return;

    Promise.all([
      apiService.getEmployeeAllowance(currentUser.id, year).catch(() => null),
      apiService.getEmployeeSchedules(currentUser.id).catch(() => []),
      // ─── FIXED: the backend's `userId` filter on /leave-requests filters
      // by `submittedBy` (who logged the request), NOT `empId` (whose leave
      // it is). That silently dropped any leave an admin/manager logged on
      // this employee's behalf. Fetch with scope:'all' (year-bounded), the
      // same way Calendar.jsx does, and filter client-side by empId below
      // instead of trusting the server-side userId filter. ───
      apiService.getLeaveRequests({
        scope: 'all',
        limit: 1000,
        from: `${year}-01-01`,
        to:   `${year}-12-31`,
      }).catch(() => null),
      apiService.getLeaveTypes().catch(() => []),
    ]).then(([allowanceRes, schedulesRes, requestsRes, typesRes]) => {
      setAllowance(allowanceRes?.data ?? allowanceRes);

      let scheds = schedulesRes?.data ?? schedulesRes ?? [];
      if (!Array.isArray(scheds)) scheds = [scheds].filter(Boolean);
      setSchedules(scheds);

      const list =
        requestsRes?.data?.requests ??
        requestsRes?.requests ??
        requestsRes ??
        null;
      // ─── FIXED: filter by empId (whose leave it is), not submittedBy ───
      setMyRequests(
        Array.isArray(list)
          ? list.filter(r => (r.empId ?? r.userId) === currentUser.id)
          : requests.filter(r => (r.empId ?? r.userId) === currentUser.id)
      );

      const types = typesRes?.data ?? typesRes ?? [];
      const typesMap = Array.isArray(types)
        ? types.reduce((acc, t) => {
            acc[t.name] = { affectsBalance: t.affectsBalance !== false, color: t.color, countsAsAbsence: (t.countsAsAbsence ?? t.countsAbsent) !== false };
            return acc;
          }, {})
        : {};
      setLeaveTypesMap(typesMap);

    }).finally(() => setLoading(false));
  }, [currentUser?.id, year]);

  const reqs = myRequests ?? [];

  const balance = (!isSA && !loading && allowance)
    ? calcDashboardAllowance({
        allowance,
        schedules,
        requests: reqs,
        leaveTypesMap,
        year,
      })
    : null;

  const balanceTotal = balance?.totalLabel ?? '—';
  const balanceUsed = balance?.usedLabel ?? '—';
  const balanceRemain = balance?.remainLabel ?? '—';
  const balancePending = balance?.pendingLabel ?? null;

  const ringUsed = balance?.ringUsed ?? 0;
  const ringTotal = balance?.ringTotal ?? 0;
  const usedPct = ringTotal > 0 ? Math.min(100, Math.round((ringUsed / ringTotal) * 100)) : 0;

  const today = new Date();
  // One source for these figures: the server summary (the same numbers the
  // calendar, sidebar, team page and mobile app show)
  const totalOnLeave = summary?.outToday?.count ?? 0;
  const outScope = summary?.outToday?.scope === 'team' ? ' in your team' : '';

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = currentUser?.name?.split(' ')[0] ?? currentUser?.firstName ?? 'there';

  // ─── My requests for the extra panels. South Africa Team users skip the
  // dashboard fetch, so fall back to the requests already in context. ───
  const mine = myRequests ?? requests.filter(r => (r.empId ?? r.userId) === currentUser?.id);
  const live = mine.filter(r => r.status === 'approved' || r.status === 'pending');
  const todayStart = startOfDay(today);

  // Upcoming (or in-progress) time off, soonest first
  const upcoming = live
    .filter(r => {
      try { return parseISO(r.to ?? r.from) >= todayStart; } catch { return false; }
    })
    .sort((a, b) => String(a.from).localeCompare(String(b.from)));
  const nextOff = upcoming.find(r => r.status === 'approved') ?? null;
  const nextOffIn = nextOff ? differenceInCalendarDays(parseISO(nextOff.from), todayStart) : null;

  const typeColor = (name, i) =>
    leaveTypesMap[name]?.color || TYPE_FALLBACK_COLORS[i % TYPE_FALLBACK_COLORS.length];

  // Days off this year (approved absences): per month for the chart and per
  // type. Working from home and other non-absence types aren't time off.
  const isAbsence = r => (leaveTypesMap[r.type ?? r.leaveType?.name]?.countsAsAbsence ?? r.leaveType?.countsAsAbsence) !== false;
  const { monthly, byType, takenTotal } = (() => {
    const monthlyArr = Array.from({ length: 12 }, () => 0);
    const typeTotals = new Map();
    let total = 0;
    mine
      .filter(r => r.status === 'approved' && isAbsence(r))
      .forEach(r => {
        const type = r.type ?? r.leaveType?.name ?? 'Leave';
        workingDayEntries(r, schedules).forEach(({ date, amount }) => {
          if (date.getFullYear() !== year) return;
          monthlyArr[date.getMonth()] += amount;
          typeTotals.set(type, (typeTotals.get(type) ?? 0) + amount);
          total += amount;
        });
      });
    const typesSorted = [...typeTotals.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([name, days], i) => ({ name, days, color: typeColor(name, i) }));
    return { monthly: monthlyArr, byType: typesSorted, takenTotal: total };
  })();
  const monthMax = Math.max(1, ...monthly);
  const [hoverMonth, setHoverMonth] = useState(null);

  // At-a-glance figures, shown as a compact list next to the balance.
  // Approvers: what's waiting for them and what they've approved. Everyone
  // else: their own requests waiting. "Approved this year" is approvers only.
  const waiting = isApprover ? (summary?.pendingForMe ?? 0) : (summary?.myPending ?? 0);
  // Each row opens the page behind the number
  const glance = [
    {
      label: isApprover ? 'Waiting for your approval' : 'Your requests waiting', value: summary ? waiting : '—', icon: Clock, tint: 'var(--warn-bg)', ink: 'var(--warn-text)', warn: waiting > 0,
      to: isApprover ? '/requests?view=inbox' : '/requests?view=mine&status=pending&when=any',
      hint: isApprover ? 'Open the requests waiting for you' : 'Open your requests waiting for approval',
    },
    {
      label: `Out today${outScope}`, value: summary ? totalOnLeave : '—', icon: Users, tint: 'var(--tint)', ink: 'var(--accent)',
      to: '/calendar', hint: 'Open the calendar at today',
    },
    ...(isApprover ? [{
      label: `Approved this year${outScope ? ' (your team)' : ''}`, value: summary?.approvedThisYear ?? '—', icon: CheckCircle, tint: 'var(--ok-bg)', ink: 'var(--ok-text)',
      to: `/requests?view=team&status=approved&when=year`, hint: 'Open the approved requests for this year',
    }] : []),
  ];

  return (
    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`
        .db-card { background: var(--surface); border: 1px solid var(--border); border-radius: 24px; box-shadow: var(--shadow); min-width: 0; transition: background-color 0.2s, border-color 0.2s; }
        .db-card-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; padding: 16px 20px; border-bottom: 1px solid var(--border-soft); }
        .db-card-title { margin: 0; font-size: 15px; font-weight: 600; color: var(--text); }
        .db-eyebrow { font-size: 11px; font-weight: 700; color: var(--text-muted); text-transform: uppercase; letter-spacing: 0.08em; }
        .db-card-icon { width: 28px; height: 28px; border-radius: 9px; display: inline-flex; align-items: center; justify-content: center; background: var(--tint); color: var(--accent); flex-shrink: 0; }

        .db-btn {
          height: 42px; padding: 0 18px; display: inline-flex; align-items: center; justify-content: center; gap: 7px;
          border: 1px solid #FFC81E; border-radius: 12px; background: #FFC81E; color: #1C1A14;
          font-size: 13.5px; font-weight: 600; white-space: nowrap; cursor: pointer; flex-shrink: 0;
          transition: background 0.15s, border-color 0.15s;
        }
        .db-btn:hover { background: #F5A300; border-color: #F5A300; }
        .db-btn:focus-visible, .db-link:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; }
        .db-link { border: none; background: none; padding: 4px 8px; margin: -4px -8px; border-radius: 8px; color: var(--accent); font-size: 12.5px; font-weight: 600; cursor: pointer; white-space: nowrap; }
        .db-link:hover { background: var(--tint); }

        .db-top { display: grid; grid-template-columns: minmax(0, 1.6fr) minmax(260px, 1fr); gap: 16px; }
        .db-top-sa { grid-template-columns: 1fr; }
        .db-bottom { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; }
        @media (max-width: 1023px) { .db-top, .db-bottom { grid-template-columns: 1fr; } }

        .db-figs { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; }
        .db-fig { padding: 11px 13px; border-radius: 14px; background: var(--surface-2); border: 1px solid var(--border-soft); min-width: 0; }
        .db-fig-val { margin: 0; font-family: 'Rubik', 'DM Sans', sans-serif; font-size: 21px; font-weight: 600; letter-spacing: -0.01em; line-height: 1.15; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .db-fig-label { margin: 3px 0 0; font-size: 11.5px; font-weight: 500; color: var(--text-muted); }

        .db-glance-row { display: flex; align-items: center; gap: 12px; padding: 12px 20px; width: 100%; border: none; background: transparent; font: inherit; text-align: left; cursor: pointer; transition: background-color 0.15s; }
        .db-glance-row:hover { background: var(--hover) !important; }
        .db-glance-row:focus-visible { outline: 2px solid var(--accent); outline-offset: -2px; }
        .db-glance-go { color: var(--text-faint); flex-shrink: 0; transition: transform 0.15s, color 0.15s; }
        .db-glance-row:hover .db-glance-go { color: var(--text-2); transform: translateX(2px); }
        .db-glance-row + .db-glance-row { border-top: 1px solid var(--border-soft); }
        .db-glance-icon { width: 34px; height: 34px; border-radius: 10px; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .db-glance-val { font-family: 'Rubik', 'DM Sans', sans-serif; font-size: 19px; font-weight: 600; }
        .db-glance-sa { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); }
        .db-glance-sa .db-glance-row + .db-glance-row { border-top: none; border-left: 1px solid var(--border-soft); }
        @media (max-width: 639px) {
          .db-glance-sa { grid-template-columns: 1fr; }
          .db-glance-sa .db-glance-row + .db-glance-row { border-left: none; border-top: 1px solid var(--border-soft); }
        }

        .db-panel-body { padding: 18px 20px; }
        /* Who's out / upcoming leave render their own .card — flatten it inside our card */
        .db-panel-body > .card { background: transparent; border: none; padding: 0; border-radius: 0; }
        .db-panel-body h3 { color: var(--text) !important; font-family: 'Rubik', 'DM Sans', sans-serif; font-size: 15px; font-weight: 600; }

        .db-pill { display: inline-flex; align-items: center; height: 20px; padding: 0 8px; border-radius: 10px; background: var(--border); color: var(--text-2); font-size: 11.5px; font-weight: 700; }
        .db-pill-warn { background: var(--warn-bg); color: var(--warn-text); }

        .db-row, .db-approval { display: flex; align-items: center; gap: 12px; padding: 11px 20px; }
        .db-row + .db-row, .db-approval + .db-approval { border-top: 1px solid var(--border-soft); }
        .db-row-title { margin: 0; font-size: 13.5px; font-weight: 600; color: var(--text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .db-row-sub { margin: 2px 0 0; font-size: 12px; color: var(--text-muted); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .db-date { width: 44px; height: 46px; border-radius: 12px; background: var(--surface-2); border: 1px solid var(--border); border-left-width: 4px; display: inline-flex; flex-direction: column; align-items: center; justify-content: center; flex-shrink: 0; box-sizing: border-box; }
        .db-status { font-size: 11.5px; font-weight: 600; padding: 2px 9px; border-radius: 10px; background: var(--ok-bg); color: var(--ok-text); border: 1px solid var(--ok-border); flex-shrink: 0; }
        .db-status-pending { background: var(--warn-bg); color: var(--warn-text); border-color: var(--warn-border); }
        .db-avatar { width: 34px; height: 34px; border-radius: 50%; background: var(--avatar-bg); color: var(--avatar-text); font-size: 12px; font-weight: 700; display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
        .db-approvals { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .db-approvals .db-approval { border-top: 1px solid var(--border-soft); }
        .db-approvals .db-approval:nth-child(-n+2) { border-top: none; }
        .db-approvals .db-approval:nth-child(odd) { border-right: 1px solid var(--border-soft); }
        @media (max-width: 1023px) {
          .db-approvals { grid-template-columns: 1fr; }
          .db-approvals .db-approval:nth-child(2) { border-top: 1px solid var(--border-soft); }
          .db-approvals .db-approval:nth-child(odd) { border-right: none; }
        }

        /* The one yellow tile: your next break */
        .db-next { display: flex; align-items: center; gap: 14px; margin: 16px 20px 6px; padding: 14px 16px; border-radius: 18px; background: #FFC81E; color: #1C1A14; }
        .db-next p { color: #1C1A14 !important; }
        .db-next p + p { color: rgba(28,26,20,0.72) !important; }
        .db-next-count { width: 56px; height: 56px; border-radius: 14px; background: #1C1A14; color: #FFC81E; display: inline-flex; flex-direction: column; align-items: center; justify-content: center; flex-shrink: 0; font-family: 'Rubik', 'DM Sans', sans-serif; }

        .db-empty { display: flex; flex-direction: column; align-items: center; gap: 4px; margin: 16px 20px 20px; padding: 26px 12px; border: 1.5px dashed var(--border); border-radius: 16px; color: var(--text-muted); font-size: 13px; text-align: center; }
        .db-empty svg { opacity: 0.6; margin-bottom: 4px; }

        .db-chart { display: flex; align-items: stretch; gap: 2px; height: 128px; }
        .db-col { flex: 1; display: flex; flex-direction: column; min-width: 0; cursor: default; }
        .db-col-plot { position: relative; flex: 1; display: flex; align-items: flex-end; justify-content: center; border-bottom: 1px solid var(--border); }
        .db-bar { width: min(22px, 70%); border-radius: 6px 6px 0 0; transition: height 0.4s, opacity 0.15s; }
        .db-bar-zero { width: min(22px, 70%); height: 2px; border-radius: 1px; background: var(--border); }
        .db-col-tip { position: absolute; bottom: calc(100% - 4px); left: 50%; transform: translateX(-50%); padding: 2px 6px; border-radius: 6px; background: var(--tip-bg); color: var(--tip-text); font-size: 11px; font-weight: 600; white-space: nowrap; pointer-events: none; z-index: 2; }
        .db-col-label { margin-top: 6px; text-align: center; font-size: 11px; color: var(--text-muted); }
        @media (max-width: 479px) { .db-col-label-rest { display: none; } }

        .db-stack { display: flex; gap: 2px; height: 8px; border-radius: 4px; overflow: hidden; }
        .db-stack span { min-width: 4px; }
        .db-types { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px 18px; margin-top: 12px; }
        .db-type { display: flex; align-items: center; gap: 8px; min-width: 0; }
        @keyframes dbSpin { to { transform: rotate(360deg); } }
        @keyframes dbShimmer { 0% { background-position: -200px 0; } 100% { background-position: 200px 0; } }
        .db-skel { display: inline-block; background: linear-gradient(90deg, var(--skel-a) 0, var(--skel-b) 50%, var(--skel-a) 100%); background-size: 400px 100%; animation: dbShimmer 1.2s linear infinite; border-radius: 8px; }
      `}</style>

      {/* ─── Header: compact greeting instead of a tall banner ─── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', paddingTop: 2 }}>
        <div style={{ minWidth: 0 }}>
          <h1 style={{ margin: 0, fontSize: 26, fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--text)' }}>
            {getGreeting()}, {firstName}
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--text-muted)' }}>
            {format(today, 'EEEE d MMMM yyyy')}
            {!summary ? '' : totalOnLeave > 0 ? ` · ${totalOnLeave} ${totalOnLeave === 1 ? 'person' : 'people'} off today${outScope}` : ` · everyone${outScope} is in today`}
          </p>
        </div>
        <button onClick={() => setShowForm(true)} className="db-btn">
          <Plus size={15} strokeWidth={2.6} />
          New request
        </button>
      </div>

      {/* ─── Top row: my leave + at a glance ─── */}
      <div className={'db-top' + (isSA ? ' db-top-sa' : '')}>
        {/* South Africa Team: leave isn't tracked against a balance here,
        so the balance card is left out and "at a glance" spans the row */}
        {!isSA && (
          <section className="db-card">
            <div className="db-card-head">
              <div style={{ minWidth: 0 }}>
                <span className="db-eyebrow">My leave · {year}</span>
              </div>
              <button className="db-link" onClick={() => setShowForm(true)}>Request leave</button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 20, padding: '16px 18px', flexWrap: 'wrap' }}>
              {loading ? (
                <>
                  <span className="db-skel" style={{ width: 88, height: 88, borderRadius: '50%' }} />
                  <div className="db-figs" style={{ flex: 1, minWidth: 240 }}>
                    {[0, 1, 2].map(i => <span key={i} className="db-skel" style={{ height: 58, borderRadius: 12 }} />)}
                  </div>
                </>
              ) : (
                <>
                  <div style={{ flexShrink: 0 }}>
                    <BalanceRing used={ringUsed} balance={ringTotal} size={88} label={balanceRemain} />
                  </div>
                  <div style={{ flex: 1, minWidth: 240, display: 'flex', flexDirection: 'column', gap: 10 }}>
                    <div className="db-figs">
                      {[
                        { label: 'Allowance',       val: balanceTotal,  color: 'var(--text)', tip: 'Your annual leave allowance for this leave year, including anything carried over' },
                        { label: 'Annual leave used', val: balanceUsed, color: 'var(--bad-text)', tip: 'Approved annual leave this leave year' },
                        { label: 'Remaining',       val: balanceRemain, color: 'var(--ok-text)', tip: 'Allowance minus approved annual leave (leave waiting for approval is shown below)' },
                      ].map(({ label, val, color, tip }) => (
                        <div key={label} className="db-fig" title={tip}>
                          <p className="db-fig-val" style={{ color }}>{val}</p>
                          <p className="db-fig-label">{label}</p>
                        </div>
                      ))}
                    </div>
                    {ringTotal > 0 && (
                      <div>
                        <div aria-hidden="true" style={{ height: 6, borderRadius: 3, background: 'var(--track)', overflow: 'hidden' }}>
                          <div style={{ width: `${usedPct}%`, height: '100%', borderRadius: 3, background: usedPct > 80 ? 'var(--bad-text)' : usedPct > 60 ? '#F5A300' : 'var(--accent)', transition: 'width 0.5s' }} />
                        </div>
                        <p style={{ margin: '5px 0 0', fontSize: 11.5, color: 'var(--text-muted)' }}>{usedPct}% of this year's allowance used</p>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>

            {/* Pending approval note */}
            {balancePending && !loading && (
              <div style={{ margin: '0 18px 16px', display: 'flex', alignItems: 'center', gap: 8, padding: '9px 12px', borderRadius: 10, background: 'var(--warn-bg-soft)', border: '1px solid var(--warn-border)', fontSize: 12.5, color: 'var(--warn-text)' }}>
                <AlertCircle size={15} style={{ color: 'var(--warn-text)', flexShrink: 0 }} />
                <span><strong>{balancePending}</strong> waiting for approval. It comes off your balance once approved.</span>
              </div>
            )}
          </section>
        )}

        <section className="db-card" style={{ overflow: 'hidden' }}>
          <div className="db-card-head">
            <span className="db-eyebrow">At a glance</span>
          </div>
          <div className={isSA ? 'db-glance-sa' : undefined}>
            {glance.map(({ label, value, icon: Icon, tint, ink, warn, to, hint }) => (
              <button key={label} type="button" className="db-glance-row" title={hint} aria-label={`${label}: ${value}. ${hint}`} onClick={() => navigate(to)} style={warn ? { background: 'var(--warn-bg-soft)' } : undefined}>
                <span className="db-glance-icon" style={{ background: tint, color: ink }}><Icon size={16} /></span>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</span>
                <span className="db-glance-val" style={{ color: warn ? 'var(--warn-text)' : 'var(--text)' }}>{value}</span>
                <ChevronRight size={15} className="db-glance-go" aria-hidden="true" />
              </button>
            ))}
          </div>
        </section>
      </div>

      {/* ─── Approvers (admins and managers): requests waiting on them ─── */}
      {isApprover && <ApprovalPanel />}

      {/* ─── My upcoming time off + my leave this year ─── */}
      <div className="db-bottom">
        <section className="db-card" style={{ overflow: 'hidden' }}>
          <div className="db-card-head">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="db-card-icon"><Plane size={15} /></span>
              <h2 className="db-card-title">My upcoming time off</h2>
            </div>
            <button className="db-link" onClick={() => setShowForm(true)}>Book</button>
          </div>

          {nextOff && (
            <div className="db-next">
              <div className="db-next-count">
                <span style={{ fontSize: 22, fontWeight: 700, lineHeight: 1, color: '#FFC81E' }}>{nextOffIn <= 0 ? 'Now' : nextOffIn}</span>
                {nextOffIn > 0 && <span style={{ fontSize: 10.5, fontWeight: 600, marginTop: 3 }}>{nextOffIn === 1 ? 'day' : 'days'}</span>}
              </div>
              <div style={{ minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: 13.5, fontWeight: 600 }}>
                  {nextOffIn <= 0 ? "You're off right now" : nextOffIn === 1 ? 'Your next break starts tomorrow' : 'until your next break'}
                </p>
                <p style={{ margin: '2px 0 0', fontSize: 12.5 }}>{nextOff.type ?? 'Leave'} · {fmtRange(nextOff.from, nextOff.to ?? nextOff.from)}</p>
              </div>
            </div>
          )}

          {loading && !isSA ? (
            <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
              {[0, 1, 2].map(i => <span key={i} className="db-skel" style={{ height: 40 }} />)}
            </div>
          ) : upcoming.length === 0 ? (
            <div className="db-empty">
              <CalendarDays size={20} />
              <span style={{ fontWeight: 600, color: 'var(--text-2)' }}>Nothing booked yet</span>
              <span>When you book leave it will show up here.</span>
            </div>
          ) : (
            <div>
              {upcoming.slice(0, 5).map((r, i) => {
                const days = workingDayEntries(r, schedules).reduce((n, e) => n + e.amount, 0);
                const isPending = r.status === 'pending';
                const color = typeColor(r.type ?? r.leaveType?.name, i);
                return (
                  <div key={r.id ?? i} className="db-row">
                    <span className="db-date" style={{ borderColor: color }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>{(() => { try { return format(parseISO(r.from), 'MMM'); } catch { return ''; } })()}</span>
                      <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)', lineHeight: 1.1, fontFamily: "'Rubik', 'DM Sans', sans-serif" }}>{(() => { try { return format(parseISO(r.from), 'd'); } catch { return ''; } })()}</span>
                    </span>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <p className="db-row-title">{r.type ?? r.leaveType?.name ?? 'Leave'}</p>
                      <p className="db-row-sub">{fmtRange(r.from, r.to ?? r.from)} · {fmtDayCount(days)}</p>
                    </div>
                    <span className={'db-status' + (isPending ? ' db-status-pending' : '')}>
                      {isPending ? 'Pending' : 'Approved'}
                    </span>
                  </div>
                );
              })}
              {upcoming.length > 5 && (
                <p style={{ margin: 0, padding: '10px 18px', borderTop: '1px solid var(--border-soft)', fontSize: 12.5, color: 'var(--text-muted)' }}>
                  +{upcoming.length - 5} more booked later this year
                </p>
              )}
            </div>
          )}
        </section>

        <section className="db-card" style={{ overflow: 'hidden' }}>
          <div className="db-card-head">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span className="db-card-icon"><BarChart3 size={15} /></span>
              <h2 className="db-card-title">My time off in {year}</h2>
            </div>
            <span style={{ fontSize: 12.5, color: 'var(--text-2)' }} title="Approved days away of every kind (annual leave, sick leave and so on). Working from home isn't counted.">
              <b style={{ color: 'var(--text)' }}>{fmtDayCount(takenTotal)}</b> off
            </span>
          </div>

          <div style={{ padding: '14px 18px 16px' }}>
            {/* Days taken per month — one series, so one hue; this month is darker */}
            <div className="db-chart" role="img" aria-label={`Days of approved leave per month in ${year}`}>
              {monthly.map((v, m) => {
                const isNow = m === today.getMonth();
                const isFuture = m > today.getMonth();
                const h = v > 0 ? Math.max(6, (v / monthMax) * 100) : 0;
                const label = format(new Date(year, m, 1), 'MMM');
                return (
                  <div
                    key={m}
                    className="db-col"
                    onMouseEnter={() => setHoverMonth(m)}
                    onMouseLeave={() => setHoverMonth(h => (h === m ? null : h))}
                    title={`${format(new Date(year, m, 1), 'MMMM')}: ${fmtDayCount(v)}`}
                  >
                    <div className="db-col-plot">
                      {hoverMonth === m && v > 0 && <span className="db-col-tip">{fmtDayCount(v)}</span>}
                      <div className="db-bar" style={{ height: `${h}%`, background: isNow ? 'var(--chart-now)' : isFuture ? 'var(--chart-future)' : 'var(--chart-past)', opacity: hoverMonth != null && hoverMonth !== m ? 0.55 : 1 }} />
                      {v === 0 && <div className="db-bar-zero" />}
                    </div>
                    <span className="db-col-label" style={isNow ? { color: 'var(--text)', fontWeight: 700 } : undefined}>{label[0]}<span className="db-col-label-rest">{label.slice(1)}</span></span>
                  </div>
                );
              })}
            </div>

            {/* Breakdown by leave type */}
            {byType.length > 0 ? (
              <div style={{ marginTop: 16 }}>
                <div className="db-stack" aria-hidden="true">
                  {byType.map(t => (
                    <span key={t.name} style={{ flex: t.days, background: t.color }} />
                  ))}
                </div>
                <div className="db-types">
                  {byType.slice(0, 6).map(t => (
                    <div key={t.name} className="db-type">
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: t.color, flexShrink: 0 }} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--text-2)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.name}</span>
                      <span style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--text)' }}>{fmtDayCount(t.days)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p style={{ margin: '14px 0 0', fontSize: 12.5, color: 'var(--text-muted)', textAlign: 'center' }}>No approved leave taken yet this year.</p>
            )}
          </div>
        </section>
      </div>

      {/* ─── Who's out + upcoming holidays ─── */}
      <div className="db-bottom">
        <section className="db-card">
          <div className="db-panel-body">
            <WhosOut />
          </div>
        </section>
        <section className="db-card">
          <div className="db-panel-body">
            <UpcomingHolidays />
          </div>
        </section>
      </div>

      {showForm && <RequestForm onClose={() => setShowForm(false)} />}
    </div>
  );
}